// RotateNewBadgeSeen — Modern 左ツール列の回転ツールボタン (PC のみ) の「New!」バッジを
// 「初回だけ」出すための seen フラグ永続化 (infrastructure 層、canvas-rotation-2026-10-03.md)。
//
// `customCursorNewBadgeSeen.ts` / `undoBadgeSeen.ts` / `pastCanvasSeen.ts` と同流儀で、
// 別 localStorage key で独立管理する。消える条件は「一度でも回転した」
// (= ボタン / R / Shift+Space / 2 本指ひねり / 左右回転のどの経路でも)。
// 不在 / storage 不可は「未 seen」(= まだ出す)。例外を throw しない。
//
// Tests: rotateNewBadgeSeen.test.ts (storage-injection style)。

/** localStorage キー: 回転ボタンの New! バッジを消したか (= 一度でも回転したか). */
export const ROTATE_NEW_BADGE_SEEN_KEY = 'oekaki:rotate-new-seen';

/** Minimal Storage shape this module uses. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface RotateNewBadgeSeenStore {
  hasSeen(): boolean;
  markSeen(): void;
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:rotate-new-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateRotateNewBadgeSeenStoreOptions {
  /** Override storage. `null` forces the no-op path. Default: probe localStorage. */
  storage?: StorageLike | null;
}

export const createRotateNewBadgeSeenStore = (
  opts: CreateRotateNewBadgeSeenStoreOptions = {},
): RotateNewBadgeSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(ROTATE_NEW_BADGE_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(ROTATE_NEW_BADGE_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(ROTATE_NEW_BADGE_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
